import { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import {
  type Contact,
  type Supplier,
  type SupplierDBResult,
} from '../types/types';
import { captureSupabaseError } from '../lib/sentry';

export const useGetSupplier = (supplierId: string) => {
  const [supplier, setSupplier] = useState<Supplier | null>(null);
  const [industries, setIndustries] = useState<string[]>([]);
  const [contacts, setContacts] = useState<Contact[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    if (!supplierId) return;

    fetchSupplier();
    fetchContacts();
  }, [supplierId]);

  const fetchSupplier = async () => {
    setLoading(true);
    setError(null);

    try {
      // --- 🔑 Fetch ALL Data in ONE Single Query using Nested Relationships ---
      const { data: supplierData, error: supplierError } = await supabase
        .from('suppliers')
        .select(
          `
            id,
            company_name,
            trading_name,
            registration_number,
            tax_id,
            date_established,
            annual_turnover,
            bank_name,
            bank_branch,
            bank_account_number,
            iban,
            swift_code,
            payment_terms,
            updated_at,
            created_at,
            business_type,
            contact_name,
            email,
            phone,
            
            primary_address:primary_address_id ( 
                id, street, city, postal_code, state, country, address_type
            ),

            supplier_industries (
                industry_id
            )
          `
        )
        .eq('id', supplierId)
        .single();

      if (supplierError) throw supplierError;

      const rawData = supplierData as unknown as SupplierDBResult;

      // --- Data Extraction and Formatting ---

      // 1. Extract Industry IDs (uses the property on the rawData object)
      const industryIds = rawData.supplier_industries
        ? rawData.supplier_industries.map((item) => item.industry_id)
        : [];
      setIndustries(industryIds);

      // 3. Construct the final supplier object to set to state (Supplier type)
      // We ensure we only keep fields defined on the clean Supplier type.
      const finalSupplier: Supplier = {
        ...rawData,
        // ⚠️ Ensure primary_address is copied if your clean Supplier type includes it,
        // otherwise, TypeScript will complain here too.
        primary_address: rawData.primary_address,
        // Explicitly delete/omit the fields that don't belong on the final Supplier state
        supplier_industries: undefined,
      } as Supplier; // Final cast to the clean Supplier type

      setSupplier(finalSupplier);
    } catch (err) {
      captureSupabaseError(err, { op: 'supplier.fetch' });
      console.error(err);
      if (err instanceof Error) setError(err);
      else setError(err as Error);
    } finally {
      setLoading(false);
    }
  };

  const fetchContacts = async () => {
    try {
      const { data, error } = await supabase
        .from('supplier_contact')
        .select(
          `
        contact:contact_id (
          id,
          name,
          email,
          phone,
          contact_type,
          created_at,
          updated_at
        ),
        role
      `
        )
        .eq('supplier_id', supplierId);

      if (error) throw error;

      // Flatten result: each row looks like { contact: {...}, role: ... }
      const extractedContacts: Contact[] =
        (data?.map((row) => ({
          ...row.contact,
          role: row.role, // optional if your Contact type includes role
        })) as unknown as Contact[]) ?? [];

      setContacts(extractedContacts);
    } catch (err) {
      captureSupabaseError(err, { op: 'supplier.contacts.fetch' });
      console.error(err);
      setError(err as Error);
    }
  };

  /** Update supplier fields locally */
  const mutate = (updatedFields: Partial<Supplier>) => {
    setSupplier((prev) => (prev ? { ...prev, ...updatedFields } : prev));
  };

  /** Update industries locally */
  const mutateIndustries = (updatedIndustries: string[]) => {
    setIndustries(updatedIndustries);
  };

  /** Update contacts locally */
  const mutateContacts = (updatedContacts: Contact[]) => {
    setContacts(updatedContacts);
  };

  return {
    supplier,
    industries,
    contacts,
    loading,
    error,
    mutate,
    mutateIndustries,
    mutateContacts,
    fetchContacts,
  };
};
